/**
 * HYPSEYA — Relational Regulation
 * Partner / family co-regulation mode.
 * Share nervous system states · get support guidance · practice together.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Character definitions (HYPSEYA NS states) ────────────────────────────────
const NS_CHARS = {
  remi:  { label: 'REMI',  desc: 'Regulated · Window of Tolerance', color: '#4AACA8', bg: 'rgba(74,172,168,0.12)',  ring: 'rgba(74,172,168,0.3)'  },
  spike: { label: 'SPIKE', desc: 'Fight · Sympathetic activated',    color: '#E07040', bg: 'rgba(224,112,64,0.1)',  ring: 'rgba(224,112,64,0.3)'  },
  flo:   { label: 'FLO',   desc: 'Freeze · Dorsal vagal collapse',  color: '#9B8EC4', bg: 'rgba(155,142,196,0.1)', ring: 'rgba(155,142,196,0.3)' },
  dash:  { label: 'DASH',  desc: 'Flight · Sympathetic anxiety',    color: '#5B8FD4', bg: 'rgba(91,143,212,0.1)',  ring: 'rgba(91,143,212,0.3)'  },
  sofi:  { label: 'SOFI',  desc: 'Fawn · People-pleasing',          color: '#E0907A', bg: 'rgba(224,144,122,0.1)', ring: 'rgba(224,144,122,0.3)' },
};

function StateOrb({ state, size = 72, pulse = false }) {
  const cfg = NS_CHARS[state] || NS_CHARS.remi;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      {pulse && (
        <div style={{
          position: 'absolute', inset: -8, borderRadius: '50%',
          background: cfg.ring,
          animation: 'orbPulse 2.4s ease-in-out infinite',
        }} />
      )}
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: `radial-gradient(circle at 38% 35%, ${cfg.color}cc, ${cfg.color}55)`,
        border: `2px solid ${cfg.ring}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
      }}>
        <span style={{ fontSize: size * 0.22, fontWeight: '800', color: '#fff', letterSpacing: '-0.02em' }}>
          {cfg.label[0]}
        </span>
      </div>
      <style>{`@keyframes orbPulse{0%,100%{transform:scale(1);opacity:0.4}50%{transform:scale(1.12);opacity:0.7}}`}</style>
    </div>
  );
}

function StatePicker({ current, onSelect }) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
      {Object.entries(NS_CHARS).map(([key, cfg]) => (
        <button
          key={key}
          onClick={() => onSelect(key)}
          style={{
            padding: '9px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
            background: current === key ? cfg.bg : 'rgba(255,255,255,0.7)',
            color: current === key ? cfg.color : '#9AADAB',
            fontWeight: current === key ? '700' : '500',
            fontSize: '0.82rem',
            boxShadow: current === key ? `0 0 0 1.5px ${cfg.color}55` : 'none',
            transition: 'all 0.18s ease',
          }}
        >
          {cfg.label}
        </button>
      ))}
    </div>
  );
}

function SupportGuide({ guide }) {
  const [open, setOpen] = useState(false);
  if (!guide) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.95)',
      borderRadius: '16px',
      border: '1px solid rgba(184,211,207,0.35)',
      overflow: 'hidden',
      marginTop: '16px',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none',
          padding: '14px 18px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          textAlign: 'left',
        }}
      >
        <div>
          <p style={{ margin: 0, fontWeight: '700', fontSize: '0.88rem', color: '#1E3A3A' }}>{guide.title}</p>
          <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: '#9AADAB' }}>How to help right now</p>
        </div>
        <span style={{ color: '#B8D3CF', fontSize: '0.8rem' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px', borderTop: '1px solid rgba(184,211,207,0.2)' }}>
          <div style={{ paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {guide.steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(30,107,104,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.65rem', fontWeight: '700', color: '#1E6B68', marginTop: '1px',
                }}>
                  {i + 1}
                </div>
                <p style={{ margin: 0, fontSize: '0.84rem', color: '#3A5250', lineHeight: 1.6 }}>{step}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Invite form ───────────────────────────────────────────────────────────────
function InviteForm({ userId, userName, onInviteSent }) {
  const [email, setEmail]       = useState('');
  const [relType, setRelType]   = useState('partner');
  const [sending, setSending]   = useState(false);

  const send = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/relational/invite`, {
        user_id: userId, user_name: userName,
        partner_email: email.trim(), relationship_type: relType,
      });
      toast.success(`Invite sent to ${email}`);
      onInviteSent(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not send invite.';
      toast.error(msg);
    }
    setSending(false);
  };

  return (
    <div style={{
      background: 'rgba(255,255,255,0.95)', borderRadius: '20px',
      padding: '24px 22px',
      border: '1px solid rgba(184,211,207,0.35)',
      boxShadow: '0 2px 16px rgba(30,107,104,0.06)',
    }}>
      <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', color: '#1E3A3A', margin: '0 0 6px' }}>
        Invite someone to co-regulate
      </h3>
      <p style={{ margin: '0 0 20px', fontSize: '0.82rem', color: '#9AADAB', lineHeight: 1.5 }}>
        Share your nervous system state with a partner, family member, or close friend — and get guidance on supporting each other.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
        {[['partner', 'Partner'], ['family', 'Family'], ['friend', 'Friend']].map(([v, l]) => (
          <button key={v} onClick={() => setRelType(v)} style={{
            flex: 1, padding: '8px', borderRadius: '10px',
            background: relType === v ? 'rgba(30,107,104,0.1)' : 'transparent',
            border: relType === v ? '1.5px solid rgba(30,107,104,0.3)' : '1px solid rgba(184,211,207,0.5)',
            color: relType === v ? '#1E6B68' : '#9AADAB',
            fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer',
          }}>{l}</button>
        ))}
      </div>

      <input
        type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="Their HYPSEYA email address"
        style={{
          width: '100%', border: '1.5px solid rgba(184,211,207,0.5)', borderRadius: '12px',
          padding: '12px 14px', fontSize: '0.9rem', color: '#1E3A3A',
          outline: 'none', marginBottom: '14px', boxSizing: 'border-box',
          background: 'rgba(255,255,255,0.8)',
        }}
      />

      <button
        onClick={send} disabled={sending || !email.trim()}
        style={{
          width: '100%', padding: '13px', borderRadius: '14px',
          background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
          border: 'none', color: '#fff', fontSize: '0.92rem', fontWeight: '600',
          cursor: 'pointer', opacity: sending ? 0.7 : 1,
        }}
      >
        {sending ? 'Sending…' : 'Send invite'}
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function RelationalPage() {
  const navigate = useNavigate();
  const [user, setUser]         = useState(null);
  const [conn, setConn]         = useState(null);   // backend connection object
  const [loading, setLoading]   = useState(true);
  const [myState, setMyState]   = useState(null);
  const [myNote, setMyNote]     = useState('');
  const [savingState, setSavingState] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) { try { setUser(JSON.parse(u)); } catch {} }
    setTimeout(() => setIsVisible(true), 60);
  }, []);

  useEffect(() => {
    if (user?.user_id) loadConnection();
  }, [user]); // eslint-disable-line

  const loadConnection = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/relational/${user.user_id}`);
      setConn(res.data);
      if (res.data.me?.ns_state) setMyState(res.data.me.ns_state);
    } catch { /* offline */ }
    setLoading(false);
  };

  const acceptInvite = async () => {
    if (!conn?.invite_id) return;
    try {
      await axios.post(`${API}/relational/accept/${conn.invite_id}?user_id=${user.user_id}`);
      toast.success('Connected!');
      loadConnection();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept invite.');
    }
  };

  const saveMyState = async (state) => {
    setMyState(state);
    setSavingState(true);
    try {
      await axios.patch(`${API}/relational/status`, {
        user_id: user.user_id, state, note: myNote || null,
      });
      toast.success(`Status updated to ${NS_CHARS[state]?.label}`);
      loadConnection();
    } catch { toast.error('Could not update status.'); }
    setSavingState(false);
  };

  const disconnect = async () => {
    try {
      await axios.delete(`${API}/relational/disconnect/${user.user_id}`);
      toast.success('Disconnected.');
      setConn({ connected: false });
    } catch { toast.error('Could not disconnect.'); }
  };

  const partner = conn?.partner;
  const partnerState = partner?.ns_state ? NS_CHARS[partner.ns_state] : null;
  const guide = conn?.support_guide;

  const formatTime = (iso) => {
    if (!iso) return null;
    try {
      const diff = Math.round((Date.now() - new Date(iso)) / 60000);
      if (diff < 1) return 'just now';
      if (diff < 60) return `${diff}m ago`;
      return `${Math.round(diff / 60)}h ago`;
    } catch { return null; }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 55%, rgba(184,211,207,0.15) 100%)',
      padding: '28px 20px 100px', maxWidth: '520px', margin: '0 auto',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
      transition: 'opacity 0.45s ease, transform 0.45s ease',
    }}>

      {/* Header */}
      <div style={{ marginBottom: '28px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(184,211,207,0.4)',
          borderRadius: '20px', padding: '8px 16px', cursor: 'pointer',
          fontSize: '0.82rem', color: '#8FA3A0', flexShrink: 0, marginTop: '4px',
        }}>← Back</button>
        <div>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.7rem', color: '#1E3A3A', margin: '0 0 4px', fontWeight: '500',
          }}>Relational</h1>
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#9AADAB' }}>
            Co-regulation with the people you love
          </p>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9AADAB' }}>Loading…</div>
      ) : !conn?.connected ? (
        /* ── No connection ── */
        <>
          {/* Explain the feature */}
          <div style={{
            background: 'rgba(255,255,255,0.85)', borderRadius: '18px',
            padding: '22px 20px', marginBottom: '20px',
            border: '1px solid rgba(184,211,207,0.3)',
          }}>
            <p style={{ margin: '0 0 14px', fontSize: '0.9rem', color: '#3A5250', lineHeight: 1.7 }}>
              Science shows that <strong>co-regulation is more powerful than solo regulation</strong>. When we share our nervous system states with people we trust, we can support each other more effectively — and more consciously.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.values(NS_CHARS).map(cfg => (
                <span key={cfg.label} style={{
                  fontSize: '0.72rem', fontWeight: '700', padding: '3px 10px', borderRadius: '12px',
                  background: cfg.bg, color: cfg.color,
                }}>
                  {cfg.label}
                </span>
              ))}
            </div>
          </div>

          <InviteForm
            userId={user?.user_id}
            userName={user?.name}
            onInviteSent={() => loadConnection()}
          />
        </>

      ) : conn.is_pending && !conn.i_invited ? (
        /* ── Pending invite received ── */
        <div style={{
          background: 'rgba(255,255,255,0.95)', borderRadius: '20px',
          padding: '28px 22px', textAlign: 'center',
          border: '1.5px solid rgba(30,107,104,0.2)',
          boxShadow: '0 2px 20px rgba(30,107,104,0.08)',
        }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(30,107,104,0.1)', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#1E6B68" strokeWidth="1.8" strokeLinecap="round"/>
              <circle cx="9" cy="7" r="4" stroke="#1E6B68" strokeWidth="1.8"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#1E6B68" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', color: '#1E3A3A', margin: '0 0 8px' }}>
            {conn.partner?.name} invited you
          </h3>
          <p style={{ fontSize: '0.85rem', color: '#9AADAB', margin: '0 0 24px', lineHeight: 1.5 }}>
            They want to share nervous system states with you on HYPSEYA.
          </p>
          <button onClick={acceptInvite} style={{
            width: '100%', padding: '14px', borderRadius: '14px',
            background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
            border: 'none', color: '#fff', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer',
            marginBottom: '10px',
          }}>
            Accept connection
          </button>
          <button onClick={disconnect} style={{
            width: '100%', padding: '12px', borderRadius: '14px',
            background: 'transparent', border: '1px solid rgba(184,211,207,0.5)',
            color: '#9AADAB', fontSize: '0.85rem', cursor: 'pointer',
          }}>
            Decline
          </button>
        </div>

      ) : conn.is_pending && conn.i_invited ? (
        /* ── Pending — waiting for acceptance ── */
        <div style={{
          background: 'rgba(255,255,255,0.9)', borderRadius: '20px',
          padding: '28px 22px', textAlign: 'center',
          border: '1px solid rgba(184,211,207,0.3)',
        }}>
          <p style={{ fontSize: '0.88rem', color: '#9AADAB', margin: '0 0 8px' }}>Invite sent to</p>
          <p style={{ fontWeight: '700', fontSize: '1rem', color: '#1E3A3A', margin: '0 0 20px' }}>{conn.partner?.name}</p>
          <p style={{ fontSize: '0.82rem', color: '#B8D3CF' }}>Waiting for them to accept…</p>
        </div>

      ) : (
        /* ── Active connection ── */
        <>
          {/* Partner state card */}
          <div style={{
            background: partnerState ? partnerState.bg : 'rgba(255,255,255,0.9)',
            borderRadius: '20px', padding: '22px 20px',
            border: `1.5px solid ${partnerState ? partnerState.ring : 'rgba(184,211,207,0.3)'}`,
            boxShadow: '0 2px 20px rgba(30,107,104,0.06)',
            marginBottom: '16px',
            display: 'flex', alignItems: 'center', gap: '18px',
          }}>
            {partner?.ns_state ? (
              <StateOrb state={partner.ns_state} size={68} pulse />
            ) : (
              <div style={{
                width: 68, height: 68, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(184,211,207,0.2)',
                border: '1.5px dashed rgba(184,211,207,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: '1.2rem', color: '#B8D3CF' }}>?</span>
              </div>
            )}
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 3px', fontWeight: '700', fontSize: '1rem', color: '#1E3A3A' }}>
                {partner?.name}
              </p>
              {partnerState ? (
                <>
                  <p style={{ margin: '0 0 2px', fontSize: '0.88rem', fontWeight: '600', color: partnerState.color }}>
                    {partnerState.label}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#9AADAB' }}>
                    {partnerState.desc}
                    {formatTime(partner.updated_at) && ` · ${formatTime(partner.updated_at)}`}
                  </p>
                  {partner.ns_note && (
                    <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#555', fontStyle: 'italic' }}>
                      "{partner.ns_note}"
                    </p>
                  )}
                </>
              ) : (
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#B8D3CF' }}>
                  Hasn't shared their state yet
                </p>
              )}
            </div>
          </div>

          {/* Support guide */}
          {guide && <SupportGuide guide={guide} />}

          {/* My state */}
          <div style={{
            background: 'rgba(255,255,255,0.9)', borderRadius: '20px',
            padding: '20px 20px', marginTop: '16px',
            border: '1px solid rgba(184,211,207,0.3)',
          }}>
            <p style={{ margin: '0 0 14px', fontSize: '0.75rem', fontWeight: '700', color: '#9AADAB', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              How are you right now?
            </p>
            <StatePicker current={myState} onSelect={saveMyState} />
            {myState && (
              <>
                <input
                  value={myNote}
                  onChange={e => setMyNote(e.target.value)}
                  placeholder="Add a note (optional)…"
                  style={{
                    width: '100%', border: '1px solid rgba(184,211,207,0.4)',
                    borderRadius: '10px', padding: '9px 12px', fontSize: '0.85rem',
                    color: '#1E3A3A', outline: 'none', marginTop: '12px',
                    boxSizing: 'border-box', background: 'rgba(255,255,255,0.7)',
                  }}
                />
                <button
                  onClick={() => saveMyState(myState)}
                  disabled={savingState}
                  style={{
                    marginTop: '10px', padding: '10px 20px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
                    border: 'none', color: '#fff', fontSize: '0.82rem',
                    fontWeight: '600', cursor: 'pointer', opacity: savingState ? 0.7 : 1,
                  }}
                >
                  {savingState ? 'Sharing…' : `Share · ${NS_CHARS[myState]?.label}`}
                </button>
              </>
            )}
          </div>

          {/* Practice together nudge */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(30,107,104,0.07), rgba(45,139,135,0.04))',
            borderRadius: '16px', padding: '16px 18px', marginTop: '16px',
            border: '1px solid rgba(30,107,104,0.12)',
            display: 'flex', alignItems: 'center', gap: '14px',
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="#1E6B68" strokeWidth="1.4"/>
              <path d="M7 10 C7 8.3 8.3 7 10 7 C11.7 7 13 8.3 13 10 C13 11.7 11.7 13 10 13 C8.3 13 7 11.7 7 10Z" fill="#1E6B68" opacity="0.5"/>
            </svg>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 2px', fontWeight: '600', fontSize: '0.88rem', color: '#1E3A3A' }}>
                Practice together
              </p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#9AADAB' }}>
                Start today's loop and invite {partner?.name} to join
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'rgba(30,107,104,0.1)', border: '1px solid rgba(30,107,104,0.2)',
                borderRadius: '10px', padding: '8px 14px',
                color: '#1E6B68', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Start →
            </button>
          </div>

          {/* Disconnect */}
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button onClick={disconnect} style={{
              background: 'none', border: 'none', color: '#C5D4D2',
              fontSize: '0.72rem', cursor: 'pointer',
            }}>
              Disconnect from {partner?.name}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
